import React, { useState, useEffect, useCallback } from 'react';
import { Box, Card, CardContent, Typography, Button, IconButton, CircularProgress, List, ListItem, ListItemText, ListItemSecondaryAction, Divider, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Fab, Menu, MenuItem,  } from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import client from '../../api/client';
import Pagination from '../../components/Pagination';
import CustomTextField from '../../components/CustomTextField';
import { toPersianDigits } from '../../utils/numberConverter';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [formData, setFormData] = useState({
    full_name: '',
    mobile: '',
    address: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        page: currentPage,
        page_size: pageSize,
      };
      if (search) params.search = search;
      
      const response = await client.get('/orders/customers/', { params });
      setCustomers(response.data.results || []);
      setTotalPages(response.data.total_pages || 1);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error('خطا در دریافت لیست');
    } finally {
      setLoading(false);
    }
  }, [search, currentPage, pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchCustomers();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchCustomers();
  }, [currentPage, pageSize]);

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setSelectedCustomer(customer);
      setFormData({
        full_name: customer.full_name,
        mobile: customer.mobile || '',
        address: customer.address || '',
      });
    } else {
      setSelectedCustomer(null);
      setFormData({
        full_name: '',
        mobile: '',
        address: '',
      });
    }
    setMenuAnchor(null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.full_name) {
      toast.error('نام مشتری الزامی است');
      return;
    }

    setSaving(true);
    try {
      if (selectedCustomer) {
        await client.put(`/orders/customers/${selectedCustomer.id}/`, formData);
        toast.success('ویرایش شد');
      } else {
        await client.post('/orders/customers/create/', formData);
        toast.success('مشتری جدید اضافه شد');
      }
      setDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error('خطا');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;
    if (!window.confirm('آیا مطمئن هستید؟')) return;

    try {
      await client.delete(`/orders/customers/${selectedCustomer.id}/`);
      toast.success('حذف شد');
      setMenuAnchor(null);
      fetchCustomers();
    } catch (error) {
      toast.error('خطا');
    }
  };

  const handleCall = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        مشتریان
      </Typography>

      <CustomTextField         fullWidth
        placeholder="جستجو..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : customers.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">مشتری یافت نشد</Typography>
        </Card>
      ) : (
        <>
          <Card>
            <List disablePadding>
              {customers.map((customer, index) => (
                <React.Fragment key={customer.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={customer.full_name}
                      secondary={
                        <>
                          {customer.mobile && (
                            <Typography variant="body2" component="span" dir="ltr">
                              {toPersianDigits(customer.mobile)}
                            </Typography>
                          )}
                          {customer.orders_count > 0 && (
                            <Typography variant="body2" component="span" sx={{ mr: 1 }}>
                              • {toPersianDigits(customer.orders_count)} سفارش
                            </Typography>
                          )}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      {customer.mobile && (
                        <IconButton
                          color="primary"
                          onClick={() => handleCall(customer.mobile)}
                        >
                          <PhoneIcon />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={(e) => {
                          setSelectedCustomer(customer);
                          setMenuAnchor(e.currentTarget);
                        }}
                      >
                        <MoreIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Card>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </>
      )}

      {/* Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleOpenDialog(selectedCustomer)}>ویرایش</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>حذف</MenuItem>
      </Menu>

      {/* FAB */}
      <Fab
        color="primary"
        sx={{ 
          position: 'fixed', 
          bottom: { xs: 72, md: 24 }, 
          left: 24,
          bgcolor: '#d4af37',
          '&:hover': { bgcolor: '#b8960c' },
        }}
        onClick={() => handleOpenDialog()}
      >
        <AddIcon />
      </Fab>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {selectedCustomer ? 'ویرایش مشتری' : 'مشتری جدید'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <CustomTextField                 fullWidth
                label="نام و نام خانوادگی *"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField                 fullWidth
                label="موبایل"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                inputProps={{ maxLength: 11, dir: 'ltr' }}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField                 fullWidth
                label="آدرس"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>انصراف</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit} 
            disabled={saving}
            sx={{ bgcolor: '#d4af37', '&:hover': { bgcolor: '#b8960c' } }}
          >
            {saving ? <CircularProgress size={24} /> : 'ذخیره'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;
